import { useState, useEffect, useRef, memo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { initializeApp } from "firebase/app";
import { getAuth, onAuthStateChanged, signOut, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { getFirestore, doc, setDoc, onSnapshot, getDoc, serverTimestamp } from 'firebase/firestore';
import { User, Compass, Target, FileText, BarChart2, MessageSquare, LogOut, XCircle, UploadCloud, Sparkles, MapPin, Brain, Play, HelpCircle, Users, Settings, Moon, Sun, Edit3, Lightbulb, Tag } from 'lucide-react';
import rakfort from '../src/rakfort.jpg';
import ResumeBuilder from './ResumeBuilder';
import CareerPath from './CareerPath';
import JobMatching from './JobMatching';
import CoverLetterGenerator from './CoverLetterGenerator';
import ResumeScoring from './ResumeScoring';
import InterviewPrep from './InterviewPrep';
import Dashboard from './dashboard';
import Layout from './components/common/Layout';
import Auth from './auth';
import Playground from './Playground';
import InterviewQuestions from './InterviewQuestions';
import SignUp from './pages/SignUp';
import Pricing from './Pricing';
import PricingStripe from './StripePayment';
import UserProfile from './UserProfile';
import { apiCall, getErrorMessage } from './utils/api';
import ResumeRepo from './ResumeRepo';
import { Help } from './Help';
//import ContactPage from './pages/ContactPageyes';

// Import new Desktop screens and adding the keys in production
import Desktop4 from './screens/Desktop4';
import Desktop20 from './screens/Desktop20';
//import Pricing from './pages/SignupPricing';
import Desktop21 from './screens/Desktop21';
import Desktop22 from './screens/Desktop22';
import Desktop23 from './screens/Desktop23';
import Desktop24 from './screens/Desktop24';
import Desktop25 from './screens/Desktop25';
import Desktop26 from './screens/Desktop26';
//import Desktop27 from './screens/Desktop27';
import Desktop28 from './screens/Desktop28';
import Desktop29 from './screens/Desktop29';
import Desktop30 from './screens/Desktop30';
import SignupPricing from './pages/SignupPricing';
import Blogs from './pages/Blogs';
import BlogDetail from './pages/BlogDetail';
import Terms from './pages/Terms';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// ---------------------- THEME ----------------------
// Allowed colors only:rgb(177, 123, 218) (primary), #fafafa (surface), #d5e7f3 (accent)
// Tailwind classes use arbitrary color values to strictly adhere to the palette.

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

const appId = 'aspirant-suite';
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// Deep sanitize to avoid undefined in Firestore
const deepSanitize = (value) => {
  if (value === undefined || value === null) return Array.isArray(value) ? [] : (typeof value === 'string' ? '' : (typeof value === 'number' ? 0 : (typeof value === 'boolean' ? false : (Array.isArray(value) ? [] : (typeof value === 'object' ? {} : '')))));
  if (Array.isArray(value)) return value.map((v) => deepSanitize(v)).filter((v) => v !== undefined);
  if (typeof value === 'object') {
    const out = {};
    Object.keys(value).forEach((k) => {
      const v = deepSanitize(value[k]);
      if (v === undefined) {
        out[k] = '';
      } else {
        out[k] = v;
      }
    });
    return out;
  }
  return value;
};

// Strip basic markdown/bullets for clean display
const stripMarkdown = (text) => {
  if (typeof text !== 'string') return text;
  return text
    .replace(/\*\*/g, '') // Remove bold
    .replace(/\*/g, '') // Remove italic
    .replace(/`{1,3}/g, '') // Remove code blocks
    .replace(/^\s*[-•]\s+/gm, '') // Remove bullet points
    .replace(/^\s*#+\s*/gm, '') // Remove headers
    .replace(/^\s*[-=]+\s*$/gm, '') // Remove horizontal rules
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Convert links to text
    .replace(/^\s*>\s*/gm, '') // Remove blockquotes
    .replace(/\n{3,}/g, '\n\n') // Limit multiple newlines
    .trim();
};

const formatResumeDataAsText = (data) => {
  if (!data || !data.personalInfo) return "";
  let text = `Name: ${data.personalInfo.name}\nEmail: ${data.personalInfo.email}\nPhone: ${data.personalInfo.phone}\nLocation: ${data.personalInfo.location}\n\n`;
  if (data.goal) text += `## Goal\n${data.goal}\n\n`;

  text += "## Work Experience\n";
  (data.experience || []).forEach(exp => {
    text += `- ${exp.title} at ${exp.company} (${exp.period})\n ${exp.description}\n`;
  });

  text += "\n## Education\n";
  (data.education || []).forEach(edu => {
    text += `- ${edu.degree} from ${edu.school} (${edu.period})\n ${edu.description}\n`;
  });

  text += "\n## Projects\n";
  (data.projects || []).forEach(proj => {
    text += `- ${proj.title} (${proj.period})\n ${proj.description}\n`;
  });

  text += "\n## Skills\n";
  {
    const skills = Array.isArray(data.skills)
      ? data.skills.filter(Boolean).map(String)
      : (typeof data.skills === 'string' ? data.skills.split(',').map(s => s.trim()).filter(Boolean) : []);
    text += skills.join(', ');
  }

  text += "\n\n## Achievements\n";
  (data.achievements || []).forEach(ach => {
    text += `- ${ach.description}\n`;
  });

  text += "\n## Awards\n";
  (data.awards || []).forEach(award => {
    text += `- ${award.description}\n`;
  });

  text += "\n## Certificates\n";
  (data.certificates || []).forEach(cert => {
    text += `- ${cert.description}\n`;
  });

  text += "\n## Hobbies\n";
  (data.hobbies || []).forEach(hobby => {
    text += `- ${hobby.description}\n`;
  });

  return text;
};

// Router-aware content wrapper
const RoutedContent = memo(function RoutedContent({
  resumeData,
  userData,
  db,
  appId,
  analysisTrigger,
  coverLetterJd,
  interviewQuestionsJd,
  onDataChange,
  onAnalyze,
  onParseComplete,
  onCareerTabChange,
  onGenerateCoverLetter,
  onGenerateInterviewQuestions,
  onClearCoverLetterJd,
  onViewSavedQna,
  selectedHistoryItem,
  user
}) {
  const location = useLocation();
  const navigate = useNavigate();

  // Map URL paths to tab names for backward compatibility
  const pathToTab = {
    '/dashboard': 'dashboard',
    '/dashboard/': 'dashboard',
    '/dashboard/resume-builder': 'resumeBuilder',
    '/dashboard/user-profile': 'userProfile',
    '/dashboard/career-path': 'careerPath',
    '/dashboard/career-copilot': 'careerCopilot',
    '/dashboard/job-matching': 'jobMatching',
    '/dashboard/pricing': 'pricing',
    '/dashboard/cover-letter': 'coverLetter',
    '/dashboard/resume-scoring': 'resumeScoring',
    '/dashboard/interview-questions': 'interviewQuestions',
    '/dashboard/interview-prep': 'interviewPrep',
    '/dashboard/playground': 'playground',
    '/dashboard/resume-repo': 'resumeRepo',
    '/dashboard/help': 'help',
  };

  // Get active tab from current path
  const activeTab = pathToTab[location.pathname] || 'dashboard';

  // Create setActiveTab function that navigates instead
  const setActiveTab = (tab) => {
    const tabToPath = {
      'dashboard': '/dashboard',
      'resumeBuilder': '/dashboard/resume-builder',
      'userProfile': '/dashboard/user-profile',
      'careerPath': '/dashboard/career-path',
      'careerCopilot': '/dashboard/career-copilot',
      'jobMatching': '/dashboard/job-matching',
      'pricing': '/dashboard/pricing',
      'coverLetter': '/dashboard/cover-letter',
      'resumeScoring': '/dashboard/resume-scoring',
      'interviewQuestions': '/dashboard/interview-questions',
      'interviewPrep': '/dashboard/interview-prep',
      'playground': '/dashboard/playground',
      'help': '/dashboard/help',
    };
    const path = tabToPath[tab] || '/dashboard';
    navigate(path);
  };

  if (!resumeData) return null;

  return (
    <Routes>
      <Route index element={
        <Dashboard
          userData={userData}
          resumeData={resumeData}
          db={db}
          appId={appId}
          analysisTrigger={analysisTrigger}
          setActiveTab={setActiveTab}
          user={user}
          onGenerateCoverLetter={onGenerateCoverLetter}
          onGenerateInterviewQuestions={onGenerateInterviewQuestions}
          onViewSavedQna={onViewSavedQna}
        />
      } />
      <Route path="resume-builder" element={
        <ResumeBuilder
          data={resumeData}
          onDataChange={onDataChange}
          onAnalyze={onAnalyze}
          onParseComplete={onParseComplete}
        />
      } />
      <Route path="user-profile" element={
        <UserProfile
          user={user}
          userData={userData}
          resumeData={resumeData}
        />
      } />
      <Route path="resumerepo" element={
        <ResumeRepo
          user={user}
          builderData={resumeData}
          trigger={analysisTrigger}
          db={db}
          appId={appId}
        />
      } />

      <Route path="career-path" element={
        <CareerPath
          user={user}
          userData={userData}
          resumeData={resumeData}
          db={db}
          appId={appId}
          externalActiveTab="basic"
          onTabChange={onCareerTabChange}
          showOnlyBasic={true}
        />
      } />
      <Route path="career-copilot" element={
        <CareerPath
          user={user}
          userData={userData}
          resumeData={resumeData}
          db={db}
          appId={appId}
          externalActiveTab="advanced"
          showOnlyAdvanced={true}
          onTabChange={onCareerTabChange}
        />
      } />
      <Route path="job-matching" element={
        <JobMatching
          resumeData={resumeData}
          onGenerateCoverLetter={onGenerateCoverLetter}
          onGenerateInterviewQuestions={onGenerateInterviewQuestions}
          setActiveTab={setActiveTab}
          user={user}
        />
      } />
      <Route path="pricing" element={
        <Pricing
          userData={userData}
          resumeData={resumeData}
          onGenerateCoverLetter={onGenerateCoverLetter}
          onGenerateInterviewQuestions={onGenerateInterviewQuestions}
          setActiveTab={setActiveTab}
          user={user}
        />
      } />
      <Route path="cover-letter" element={
        <CoverLetterGenerator
          user={user}
          resumeData={resumeData}
          initialJobDescription={coverLetterJd}
          onClearInitialJd={onClearCoverLetterJd}
          db={db}
          appId={appId}
        />
      } />
      <Route path="resume-scoring" element={
        <ResumeScoring
          resumeData={resumeData}
          user={user}
        />
      } />
      <Route path="interview-questions" element={
        <InterviewQuestions
          jobDescription={interviewQuestionsJd}
          resumeData={resumeData}
          user={user}
          userData={userData}
          preloadedHistoryItem={selectedHistoryItem}
        />
      } />
      <Route path="interview-prep" element={
        <InterviewPrep
          resumeData={resumeData}
          user={user}
          userData={userData}
          db={db}
          appId={appId}
        />
      } />
      <Route path="playground" element={
        <Playground
          resumeData={resumeData}
          user={user}
          userData={userData}
        />
      } />
      <Route path="help" element={
        <Help setActiveTab={setActiveTab} />
      } />
    </Routes>
  );
});

// MainContent hoisted to module scope to preserve identity across AppContent re-renders
const MainContent = memo(function MainContent({
  activeTab,
  resumeData,
  userData,
  db,
  appId,
  analysisTrigger,
  coverLetterJd,
  interviewQuestionsJd,
  onDataChange,
  onAnalyze,
  onParseComplete,
  onCareerTabChange,
  onGenerateCoverLetter,
  onGenerateInterviewQuestions,
  onClearCoverLetterJd,
  setActiveTab,
  user
}) {
  // Use the onGenerate* props directly instead of handle* props
  const handleInitiateCoverLetter = onGenerateCoverLetter;
  const handleInitiateInterviewQuestions = onGenerateInterviewQuestions;
  const handleClearCoverLetterJd = onClearCoverLetterJd;
  if (!resumeData) return null; // Match previous behavior: render nothing until resume data is ready
  switch (activeTab) {
    case 'resumeBuilder':
      return (
        <ResumeBuilder
          data={resumeData}
          onDataChange={onDataChange}
          onAnalyze={onAnalyze}
          onParseComplete={onParseComplete}
        />
      );
    case 'userProfile':
      return (
        <UserProfile
          user={user}
          userData={userData}
          resumeData={resumeData}
        />
      );
    case 'careerPath':
      return (
        <CareerPath
          user={user}
          userData={userData}
          resumeData={resumeData}
          db={db}
          appId={appId}
          externalActiveTab="basic"
          onTabChange={onCareerTabChange}
          showOnlyBasic={true}
        />
      );
    case 'careerCopilot':
      return (
        <CareerPath
          user={user}
          userData={userData}
          resumeData={resumeData}
          db={db}
          appId={appId}
          externalActiveTab="advanced"
          showOnlyAdvanced={true}
          onTabChange={onCareerTabChange}
        />
      );
    case 'jobMatching':
      return (
        <JobMatching
          resumeData={resumeData}
          onGenerateCoverLetter={handleInitiateCoverLetter}
          onGenerateInterviewQuestions={handleInitiateInterviewQuestions}
          setActiveTab={setActiveTab}
          user={user}
        />
      );
    case 'pricing':
      return (
        <Pricing
          userData={userData}
          resumeData={resumeData}
          onGenerateCoverLetter={handleInitiateCoverLetter}
          onGenerateInterviewQuestions={handleInitiateInterviewQuestions}
          setActiveTab={setActiveTab}
          user={user}
        />
      );

    case 'coverLetter':
      return (
        <CoverLetterGenerator
          user={user}
          resumeData={resumeData}
          initialJobDescription={coverLetterJd}
          onClearInitialJd={onClearCoverLetterJd}
          db={db}
          appId={appId}
        />
      );
    case 'resumeScoring':
      return (
        <ResumeScoring
          user={user}
          builderData={resumeData}
          trigger={analysisTrigger}
          db={db}
          appId={appId}
        />
      );
    case 'resumeRepo':
      return (
        <ResumeRepo
          user={user}
          builderData={resumeData}
          trigger={analysisTrigger}
          db={db}
          appId={appId}
        />
      );
    case 'interviewPrep':
      return (
        <InterviewPrep
          user={user}
          userData={userData}
          resumeData={resumeData}
          db={db}
          appId={appId}
        />
      );
    case 'dashboard':
      return (
        <Dashboard
          user={user}
          userData={userData}
          resumeData={resumeData}
          db={db}
          appId={appId}
          onGenerateCoverLetter={onGenerateCoverLetter}
          onGenerateInterviewQuestions={handleInitiateInterviewQuestions}
          setActiveTab={setActiveTab}
        />
      );
    case 'playground':
      return <Playground resumeData={resumeData} userData={userData} user={user} />;
    case 'interviewQuestions':
      return <InterviewQuestions resumeData={resumeData} jobDescription={interviewQuestionsJd} userData={userData} user={user} />;
    case 'mapAnalysis':
      return (
        <CareerPath
          user={user}
          userData={userData}
          resumeData={resumeData}
          db={db}
          appId={appId}
          externalActiveTab="basic"
          showOnlyBasic={true}
        />
      );
    case 'help':
      return <Help setActiveTab={setActiveTab} />;
    default:
      return (
        <ResumeBuilder
          data={resumeData}
          onDataChange={onDataChange}
          onAnalyze={onAnalyze}
          onParseComplete={onParseComplete}
        />
      );
  }
});

// ---------------------- UI ----------------------

const LoginScreen = ({ onSignIn, onSkip }) => (
  <Auth onSignIn={onSignIn} />
);

const ResumeUploadModal = ({ isOpen, onUploadComplete, onParseComplete, onSignOut, user }) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [error, setError] = useState(null);
  const [resumeContent, setResumeContent] = useState('');

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile(file);
      setError(null);
    }
  };

  const handleParseResume = async () => {
    const hasText = typeof resumeContent === 'string' && resumeContent.trim().length > 0;
    const hasFile = !!uploadedFile;
    if (!hasFile && !hasText) {
      setError("Please upload a file or paste your resume content.");
      return;
    }

    // Validate user is authenticated
    if (!user) {
      setError("You must be signed in to upload a resume. Please sign in and try again.");
      return;
    }

    setIsParsing(true);
    setError(null);

    const formData = new FormData();
    if (hasFile) {
      formData.append('resume_file', uploadedFile);
    }
    // Include optional pasted resume content as plain text if provided
    if (hasText) {
      formData.append('resume_text', resumeContent.trim());
    }

    try {
      // Get auth token directly from the user object (more reliable than auth.currentUser)
      const { data: parsedData } = await apiCall('/api/parse-resume', {
        method: "POST",
        body: formData,
      });

      await onParseComplete(parsedData);
      onUploadComplete();
    } catch (err) {
      console.error("Error parsing resume:", err);
      setError(getErrorMessage(err));
    } finally {
      setIsParsing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[#3C0F6C]/80 flex items-center justify-center p-4 z-50">
      <div className="text-center p-8 bg-[#fafafa] border border-[#d5e7f3] rounded-2xl shadow-2xl max-w-lg w-full">
        <UploadCloud size={48} className="mx-auto text-[#3C0F6C] mb-4" />
        <h1 className="text-3xl font-bold text-[#3C0F6C] mb-2">Welcome to JobStep</h1>
        <p className="text-sm text-[#3C0F6C]/80 mb-6">To get started, please upload your resume (PDF or DOCX).</p>

        {error && (
          <p className="text-sm text-[#3C0F6C] bg-[#d5e7f3] p-3 rounded-xl mb-4">{error}</p>
        )}

        <div className="mb-6 text-left">
          <input
            id="resume-upload"
            type="file"
            onChange={handleFileChange}
            accept=".pdf,.doc,.docx"
            className="block w-full text-sm text-[#3C0F6C] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border file:border-[#d5e7f3] file:text-sm file:font-semibold file:bg-[#d5e7f3] file:text-[#3C0F6C] hover:file:bg-[#fafafa]"
          />
          {uploadedFile && (
            <p className="mt-2 text-xs text-[#3C0F6C]/80">Selected: {uploadedFile.name}</p>
          )}
        </div>

        {/* Optional pasted resume content textarea */}
        <div className="mb-6 text-left">
          <label htmlFor="resume-content" className="block text-sm font-medium text-[#3C0F6C] mb-2">
            Paste your resume content here…
          </label>
          <textarea
            id="resume-content"
            rows={6}
            value={resumeContent}
            onChange={(e) => setResumeContent(e.target.value)}
            placeholder="Paste your resume content here…"
            className="w-full text-sm text-[#3C0F6C] bg-[#fafafa] border border-[#d5e7f3] rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-[#d5e7f3] placeholder:text-[#3C0F6C]/60"
          />
        </div>

        <button
          onClick={handleParseResume}
          disabled={((!uploadedFile && !(resumeContent && resumeContent.trim().length > 0)) || isParsing)}
          className="w-full bg-[#3C0F6C] text-[#fafafa] font-semibold py-3 px-6 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#3C0F6C]/90"
        >
          {isParsing ? (
            <svg className="animate-spin h-5 w-5 text-[#fafafa]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : (
            <Sparkles size={18} />
          )}
          <span>{isParsing ? 'Analyzing Your Resume...' : 'Upload & Continue'}</span>
        </button>

        <button onClick={onSignOut} className="mt-4 text-xs text-[#3C0F6C]/80 hover:text-[#3C0F6C]">Sign Out</button>
      </div>
    </div>
  );
};

const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Map URL paths to tab names for active state
  const pathToTab = {
    '/dashboard': 'dashboard',
    '/dashboard/': 'dashboard',
    '/dashboard/resume-builder': 'resumeBuilder',
    '/dashboard/user-profile': 'userProfile',
    '/dashboard/career-path': 'careerPath',
    '/dashboard/career-copilot': 'careerCopilot',
    '/dashboard/job-matching': 'jobMatching',
    '/dashboard/pricing': 'pricing',
    '/dashboard/cover-letter': 'coverLetter',
    '/dashboard/resume-scoring': 'resumeScoring',
    '/dashboard/interview-questions': 'interviewQuestions',
    '/dashboard/interview-prep': 'interviewPrep',
    '/dashboard/playground': 'playground',
    '/dashboard/help': 'help',
  };

  const activeTab = pathToTab[location.pathname] || 'dashboard';

  // Create setActiveTab function that navigates
  const setActiveTab = (tab) => {
    const tabToPath = {
      'dashboard': '/dashboard',
      'resumeBuilder': '/dashboard/resume-builder',
      'userProfile': '/dashboard/user-profile',
      'careerPath': '/dashboard/career-path',
      'careerCopilot': '/dashboard/career-copilot',
      'jobMatching': '/dashboard/job-matching',
      'pricing': '/dashboard/pricing',
      'coverLetter': '/dashboard/cover-letter',
      'resumeScoring': '/dashboard/resume-scoring',
      'interviewQuestions': '/dashboard/interview-questions',
      'interviewPrep': '/dashboard/interview-prep',
      'playground': '/dashboard/playground',
      'help': '/dashboard/help',
    };
    const path = tabToPath[tab] || '/dashboard';
    navigate(path);
  };
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [resumeData, setResumeData] = useState(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [analysisTrigger, setAnalysisTrigger] = useState({ source: null, timestamp: null });
  const [coverLetterJd, setCoverLetterJd] = useState('');
  const [interviewQuestionsJd, setInterviewQuestionsJd] = useState('');
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  const [isInterviewQuestionsModalOpen, setIsInterviewQuestionsModalOpen] = useState(false);
  const [interviewQuestionsData, setInterviewQuestionsData] = useState(null);
  const [isLoadingInterviewQuestions, setIsLoadingInterviewQuestions] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const previousRoleRef = useRef(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      const stored = localStorage.getItem('theme');
      if (stored) return stored === 'dark';
      return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch (err) {
      console.error('Error accessing localStorage for theme:', err);
      return false;
    }
  });
  // Persist and apply theme (must be declared before any early returns)
  useEffect(() => {
    try {
      document.documentElement.classList.toggle('dark', isDarkMode);
      localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    } catch (err) {
      console.error('Error setting theme in localStorage:', err);
    }
  }, [isDarkMode]);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [careerActiveTab, setCareerActiveTab] = useState('basic');
  // Guest mode removed for security - all users must authenticate
  const guestMode = false;
  const [isSidebarPinnedOpen, setIsSidebarPinnedOpen] = useState(() => {
    try { return localStorage.getItem('sidebarPinned') === '1'; } catch (err) {
      console.error('Error accessing localStorage for sidebarPinned:', err);
      return false;
    }
  });
  // Hover-expand drawer state with delays to prevent flicker
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimers = useRef({ enter: null, leave: null });
  const isDrawerOpen = (isSidebarPinnedOpen || isHovered);

  const handleSidebarMouseEnter = () => {
    if (hoverTimers.current.leave) {
      clearTimeout(hoverTimers.current.leave);
      hoverTimers.current.leave = null;
    }
    hoverTimers.current.enter = setTimeout(() => setIsHovered(true), 150);
  };

  const handleSidebarMouseLeave = () => {
    if (hoverTimers.current.enter) {
      clearTimeout(hoverTimers.current.enter);
      hoverTimers.current.enter = null;
    }
    hoverTimers.current.leave = setTimeout(() => setIsHovered(false), 200);
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (hoverTimers.current.enter) clearTimeout(hoverTimers.current.enter);
      if (hoverTimers.current.leave) clearTimeout(hoverTimers.current.leave);
    };
  }, []);

  useEffect(() => {
    try { localStorage.setItem('sidebarPinned', isSidebarPinnedOpen ? '1' : '0'); } catch (err) {
      console.error('Error setting sidebarPinned in localStorage:', err);

    }
  }, [isSidebarPinnedOpen]);


  useEffect(() => {
    // Authentication required - no guest mode bypass
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setAuthLoading(true);
      if (currentUser) {
        setUser(currentUser);
        // SIMPLIFIED: Single source of truth - only read from namespaced location
        const userDocRef = doc(db, `artifacts/${appId}/users/${currentUser.uid}`);
        let userDocSnap;
        try {
          userDocSnap = await getDoc(userDocRef);
        } catch (error) {
          console.error("❌ ERROR reading Firestore document:", error);
          console.error("❌ Error code:", error.code);
          console.error("❌ Error message:", error.message);
          if (error.code === 'permission-denied') {
            console.error("🚫 PERMISSION DENIED: Firestore security rules are blocking read access!");
            console.error("🚫 Path:", `artifacts/${appId}/users/${currentUser.uid}`);
            alert("Permission denied reading user data. Check Firestore security rules.");
          }
          setAuthLoading(false);
          return;
        }

        if (userDocSnap.exists()) {
          // Document exists - use it directly
          const userData = userDocSnap.data();
          console.log("🔍 Initial Firestore read:", {
            exists: true,
            role: userData?.role,
            subscription: userData?.subscription,
            fullData: userData
          });
          setUserData(userData);
          previousRoleRef.current = userData?.role || 'basic';
        } else {
          // Document doesn't exist - create it with basic role
          // Backend will upgrade it to premium when payment is verified
          const newUserProfile = {
            uid: currentUser.uid,
            email: currentUser.email,
            displayName: currentUser.displayName,
            photoURL: currentUser.photoURL,
            createdAt: serverTimestamp(),
            role: 'basic'
          };
          await setDoc(userDocRef, newUserProfile, { merge: true });
          console.log("✅ Created new user document with basic role");
          setUserData(newUserProfile);
          previousRoleRef.current = 'basic';
        }

        const resumeDocRef = doc(db, `artifacts/${appId}/users/${currentUser.uid}/resume/data`);
        const resumeDocSnap = await getDoc(resumeDocRef);
        if (resumeDocSnap.exists() && resumeDocSnap.data().personalInfo?.name) {
          setIsUploadModalOpen(false);
        } else {
          setIsUploadModalOpen(true);
        }
      } else {
        setUser(null);
        setUserData(null);
        setIsUploadModalOpen(false);
      }
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // useEffect(() => {
  // let unsubscribe = () => {};

  // if (user) {
  //   // This creates a "pipe" between the Database and your App
  //   // The moment the database changes (role goes premium -> basic), 
  //   // this code runs automatically and updates the UI.
  //   unsubscribe = onSnapshot(doc(db, "users", user.uid), (snapshot) => {
  //     if (snapshot.exists()) {
  //       console.log("Database updated! New data:", snapshot.data());
  //       setUserData(snapshot.data()); // This will update the props sent to Playground
  //     }
  //   });
  // }

//   return () => unsubscribe(); // Cleanup the listener when user logs out
// }, [user]);

  // useEffect(() => {
    // Real-time user data listener - SIMPLIFIED: Single source of truth
   // Inside AppContent component in App.js

useEffect(() => {
  if (user) {
    const appId = 'aspirant-suite';
    // Listen to the namespaced path specifically
    const userDocRef = doc(db, `artifacts/${appId}/users/${user.uid}`);
    
    const unsubscribeUser = onSnapshot(userDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        // Client-side safety check: Even if the backend hasn't run yet,
        // we check the date here to flip the UI immediately.
        if (data.subscription?.expiresAt) {
          const expiryDate = data.subscription.expiresAt.toDate 
            ? data.subscription.expiresAt.toDate() 
            : new Date(data.subscription.expiresAt);
            
          if (new Date() > expiryDate && data.role === 'premium') {
            console.log("Client-side expiry detection");
            // We don't update DB here (security), but we update local state
            setUserData({ ...data, role: 'basic' });
            return;
          }
        }
        
        setUserData(data);
      }
    });

    return () => unsubscribeUser();
  }
}, [user]);

  // Foreground time tracker (per user) stored in localStorage and broadcast via 'usage-updated'
  useEffect(() => {
  if (!user) {
    setUserData(null);
    setResumeData(null);
    return;
  }

  // Define paths
  const nsUserDocRef = doc(db, `artifacts/${appId}/users/${user.uid}`);
  const resumeDocRef = doc(db, `artifacts/${appId}/users/${user.uid}/resume/data`);

  console.log("🚀 Starting single-source-of-truth listeners");

  // 1. Listen to the Namespaced User Doc (Role, Subscription, etc.)
  const unsubscribeUser = onSnapshot(nsUserDocRef, (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data();
      setUserData(data);

      const newRole = data.role || 'basic';
      const prevRole = previousRoleRef.current;

      // Detect if user was downgraded (Revocation)
      if (prevRole === 'premium' && newRole === 'basic') {
        console.log("⚠️ Access Revoked: Subscription expired.");
        // This will automatically lock the Playground because props change
      }

      // Detect if user was upgraded (Payment success)
      if (prevRole === 'basic' && newRole === 'premium') {
        setShowPaymentSuccess(true);
      }

      previousRoleRef.current = newRole;
    }
  }, (error) => console.error("User listener error:", error));

  // 2. Listen to Resume Data
  const unsubscribeResume = onSnapshot(resumeDocRef, (docSnap) => {
    if (docSnap.exists()) {
      setResumeData(docSnap.data());
    } else {
      setResumeData(null);
    }
  }, (error) => console.error("Resume listener error:", error));

  return () => {
    unsubscribeUser();
    unsubscribeResume();
  };
}, [user, appId]); 

// 3. Expiry Sync Trigger (Frontend → Backend)
useEffect(() => {
  const syncSubscription = async () => {
    if (!user || !userData) return;

    if (userData.role !== 'premium') return;

    const expiresAt = userData.subscription?.expiresAt;
    if (!expiresAt) return;

    const expiryDate = expiresAt.toDate ? expiresAt.toDate() : new Date(expiresAt);

    if (new Date() > expiryDate) {
      console.log("Triggering backend check for subscription expiry...");
      try {
        await apiCall('/api/auth/logout', { method: 'GET' }).catch(() => {});
      } catch (err) {
        console.error("Subscription sync failed:", err);
      }
    }
  };

  syncSubscription();
}, [user, userData]);


  const handleDataChange = async (section, data) => {
    if (user) {
      const resumeDocRef = doc(db, `artifacts/${appId}/users/${user.uid}/resume/data`);
      const sanitized = deepSanitize({ [section]: data, updatedAt: new Date().toISOString() });
      await setDoc(resumeDocRef, sanitized, { merge: true });
    }
  };

  const handleResumeParse = async (parsedData) => {
    if (user) {
      const resumeDocRef = doc(db, `artifacts/${appId}/users/${user.uid}/resume/data`);
      const validatedData = {
        personalInfo: parsedData.personalInfo || { name: user.displayName || '', email: user.email || '', phone: '', location: '' },
        goal: parsedData.goal || '',
        experience: Array.isArray(parsedData.experience) ? parsedData.experience : [],
        education: Array.isArray(parsedData.education) ? parsedData.education : [],
        projects: Array.isArray(parsedData.projects) ? parsedData.projects : [],
        skills: Array.isArray(parsedData.skills) ? parsedData.skills : [],
        achievements: Array.isArray(parsedData.achievements) ? parsedData.achievements : [],
        awards: Array.isArray(parsedData.awards) ? parsedData.awards : [],
        certificates: Array.isArray(parsedData.certificates) ? parsedData.certificates : [],
        hobbies: Array.isArray(parsedData.hobbies) ? parsedData.hobbies : [],
        updatedAt: new Date().toISOString()
      };
      await setDoc(resumeDocRef, deepSanitize(validatedData));
    }
  };

  const initiateAnalysisFromBuilder = () => {
    setAnalysisTrigger({ source: 'builder', timestamp: Date.now() });
    setActiveTab('resumeScoring');
  };

  const handleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Error signing in with Google:", error);
    }
  };

  const handleSignOut = async () => {
    try {
      // Call backend to revoke refresh tokens (security best practice)
      try {
        await apiCall('/api/auth/logout', { method: 'POST' });
      } catch (error) {
        // Log but don't block logout if backend call fails
        console.warn("Backend logout failed (continuing with frontend logout):", error);
      }

      // Clear Firebase auth state
      await signOut(auth);

      console.log("User logged out successfully");
    } catch (error) {
      console.error("Sign out error", error);
    }
  };

  const handleInitiateCoverLetter = (jobDescription) => {
    setCoverLetterJd(jobDescription);
    setActiveTab('coverLetter');
  };

  const handleClearCoverLetterJd = () => setCoverLetterJd('');

  const handleCareerTabChange = (tab) => {
    setCareerActiveTab(tab);
    if (tab === 'basic') {
      setActiveTab('careerPath');
    } else if (tab === 'advanced') {
      setActiveTab('careerCopilot');
    }
  };

  const handleInitiateInterviewQuestions = (jobDescription) => {
    setSelectedHistoryItem(null); // IMPORTANT: Clear old history so it doesn't show old Q&A
    setInterviewQuestionsJd(jobDescription);
    navigate('/dashboard/interview-questions');
  };

  const handleViewSavedQna = (item) => {
    setSelectedHistoryItem(item); // Save the full history object (with sections)
    setInterviewQuestionsJd(item.jobDescription); // Keep text in sync
    navigate('/dashboard/interview-questions'); // Move to the Q&A page
  };

  const handleGenerateInterviewQuestions = async (jobDescription) => {
    setIsInterviewQuestionsModalOpen(true);
    setIsLoadingInterviewQuestions(true);
    setInterviewQuestionsData(null);

    const formData = new FormData();
    formData.append('job_description_text', jobDescription);
    formData.append('resume_text', formatResumeDataAsText(resumeData));

    try {
      const { data } = await apiCall('/api/generate-qna', { method: 'POST', body: formData });
      setInterviewQuestionsData(data.qna_list);
    } catch (error) {
      console.error("Error generating interview questions:", error);
      setInterviewQuestionsData([{ question: "Error", answer: "Could not generate questions. Please try again." }]);
    } finally {
      setIsLoadingInterviewQuestions(false);
    }
  };


  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#fafafa]">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-[#3C0F6C] mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-[#3C0F6C]">Initializing JobStep...</p>
        </div>
      </div>
    );
  }

  // Require authentication - no guest mode for security
  if (!user) {
    return <LoginScreen onSignIn={handleSignIn} />;
  }

  if (!resumeData && !isUploadModalOpen) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#fafafa]">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-[#3C0F6C] mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-[#3C0F6C]">Loading your resume data...</p>
        </div>
      </div>
    );
  }



  return (
    <div className={`min-h-screen font-sans`}>
      <div className="flex flex-row min-h-screen">
        {/* Sidebar */}
        <aside
          onPointerEnter={handleSidebarMouseEnter}
          onPointerLeave={handleSidebarMouseLeave}
          className={`${isDrawerOpen ? 'w-64' : 'w-16'} fixed left-0 top-0 h-[100dvh] z-50 shrink-0 bg-[#3c0f6c] transition-all duration-300 ease-in-out overflow-y-auto group`}
          style={{ backdropFilter: 'blur(8px)', color: 'white', borderRight: '1px solid var(--rk-border)' }}
        >
          {/* Logo + Title + Toggle Button */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2 min-w-0 px-3 py-3">
              <img
                src={rakfort}
                alt="Logo"
                className="w-8 h-8 object-contain flex-shrink-0"
              />
              <h1
                className={`text-2xl font-bold truncate transition-all duration-300 ease-in-out ${isDrawerOpen ? 'opacity-100 ml-2' : 'opacity-0 ml-0 pointer-events-none'}`}
                style={{ color: 'var(--color-accent)' }}
              >
                JobStep
              </h1>
            </div>
            <div className="flex justify-center px-3 py-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsSidebarPinnedOpen(v => !v);
                }}
                className={`flex items-center justify-center w-8 h-8 rounded-full bg-[#3c0f6c] border border-[#5d3a8f] shadow-md hover:bg-[#4d1a8c] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FFD600] ${isDrawerOpen ? 'rotate-180' : ''}`}
                aria-label={isDrawerOpen ? 'Collapse sidebar' : 'Expand sidebar'}
              >
                <svg
                  className="w-3.5 h-3.5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>


          {/* User Info with Profile Dropdown */}
          <div className="relative mb-8 px-2">
            <button
              onClick={() => setShowProfileDropdown(!showProfileDropdown)}
              className="flex items-center gap-3 w-full p-2 rounded-xl hover:bg-[#fafafa]/10 transition-colors"
            >
              <img
                src={user.photoURL || 'https://placehold.co/40x40/1a4673/fafafa?text=U'}
                alt="User Avatar"
                className="w-10 h-10 rounded-full border border-[#d5e7f3] object-cover flex-shrink-0"
              />
              <div className={`min-w-0 flex-1 text-left transition-all duration-300 ease-in-out ${isDrawerOpen ? 'opacity-100 ml-1' : 'opacity-0 ml-0 pointer-events-none'}`}>
                <p className="text-sm font-semibold truncate">{user.displayName}</p>
                <p className="text-xs opacity-80 capitalize truncate">{userData?.role} User</p>
              </div>
              <span className={`${isDrawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'} transition-opacity duration-300 ease-in-out`}>
                <Edit3 size={16} className="opacity-60" />
              </span>
            </button>

            {/* Profile Dropdown */}
            {showProfileDropdown && isDrawerOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white text-[#0E0E0E] rounded-xl shadow-lg border border-[#E5E5E5] z-50">
                <button
                  onClick={() => {
                    setActiveTab('resumeBuilder');
                    setShowProfileDropdown(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#FFD600] hover:text-[#0E0E0E] rounded-t-xl"
                >
                  <User size={20} />
                  <span>AI Resume Builder</span>
                </button>
                <button
                  onClick={() => {
                    setActiveTab('userProfile');
                    setShowProfileDropdown(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#FFD600] hover:text-[#0E0E0E]"
                >
                  <User size={20} />
                  <span>User Details</span>
                </button>

                <div className="border-t border-[#d5e7f3]">
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#FFD600] hover:text-[#0E0E0E] rounded-b-xl"
                  >
                    <LogOut size={20} />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="space-y-4 flex-grow min-w-0 overflow-x-hidden">
            {/* Dashboard Section */}

            <div>
              <NavItem icon={<MessageSquare size={20} />} label="Dashboard" showText={isDrawerOpen} active={activeTab === 'dashboard'} onClick={() => setActiveTab('dashboard')} isDarkMode={isDarkMode} />
            </div>

            {/* Career Section */}
            <div>
              <div className={`text-xs font-semibold text-white/80 uppercase tracking-wider mb-2 px-4 transition-opacity duration-300 ease-in-out ${isDrawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>Career</div>
              <div className="space-y-1">
                <NavItem icon={<MapPin size={20} />} label="Map Analysis" showText={isDrawerOpen} active={activeTab === 'careerPath'} onClick={() => handleCareerTabChange('basic')} isDarkMode={isDarkMode} />
                <NavItem icon={<Brain size={20} />} label="Career Copilot" showText={isDrawerOpen} active={activeTab === 'careerCopilot'} onClick={() => handleCareerTabChange('advanced')} isDarkMode={isDarkMode} />
                <NavItem icon={<Target size={20} />} label="Job Matching" showText={isDrawerOpen} active={activeTab === 'jobMatching'} onClick={() => setActiveTab('jobMatching')} isDarkMode={isDarkMode} />

              </div>
            </div>

            {/* Interview Section */}
            <div>
              <div className={`text-xs font-semibold text-[#fafafa]/60 uppercase tracking-wider mb-2 px-4 transition-opacity duration-300 ease-in-out ${isDrawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>Interview</div>
              <div className="space-y-1">
                <NavItem icon={<Sparkles size={20} />} label="Interview Copilot" showText={isDrawerOpen} active={activeTab === 'playground'} onClick={() => setActiveTab('playground')} isDarkMode={isDarkMode} />
                <NavItem icon={<Lightbulb size={20} />} label="Interview AI Questions" showText={isDrawerOpen} active={activeTab === 'interviewQuestions'} onClick={() => setActiveTab('interviewQuestions')} isDarkMode={isDarkMode} />
                <NavItem icon={<Play size={20} />} label="Mock Interview" showText={isDrawerOpen} active={activeTab === 'interviewPrep'} onClick={() => setActiveTab('interviewPrep')} isDarkMode={isDarkMode} />
              </div>
            </div>

            {/* Tools Section */}
            <div>
              <div className={`text-xs font-semibold text-[#fafafa]/60 uppercase tracking-wider mb-2 px-4 transition-opacity duration-300 ease-in-out ${isDrawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>Tools</div>
              <div className="space-y-1">
                <NavItem icon={<FileText size={20} />} label="Cover Letter" showText={isDrawerOpen} active={activeTab === 'coverLetter'} onClick={() => setActiveTab('coverLetter')} isDarkMode={isDarkMode} />
                <NavItem icon={<BarChart2 size={20} />} label="Resume Scoring" showText={isDrawerOpen} active={activeTab === 'resumeScoring'} onClick={() => setActiveTab('resumeScoring')} isDarkMode={isDarkMode} />
                {/* <NavItem icon={<Target size={20} />} label="Job Matching" showText={(isSidebarPinnedOpen || isSidebarHovered)} active={activeTab === 'jobMatching'} onClick={() => setActiveTab('jobMatching')} isDarkMode={isDarkMode} /> */}
              </div>
            </div>
            <NavItem icon={<Tag size={20} />} label="Pricing" showText={isDrawerOpen} active={activeTab === 'pricing'} onClick={() => setActiveTab('pricing')} isDarkMode={isDarkMode} />
            <NavItem icon={<HelpCircle size={20} />} label="Help" showText={isDrawerOpen} active={activeTab === 'help'} onClick={() => setActiveTab('help')} isDarkMode={isDarkMode} />

          </nav>

          {/* Dark Mode Toggle */}
          <div className="mt-8 flex-shrink-0">
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="w-full text-sm font-semibold py-2.5 rounded-xl transition-colors border"
              style={{ backgroundColor: 'var(--color-accent)', color: '#0E0E0E', borderColor: 'var(--color-accent)' }}
            >
              <div className="flex items-center justify-center gap-2">
                {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
                <span className={`hidden ${(isDrawerOpen ? 'md:inline' : 'md:hidden')}`}>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
              </div>
            </button>
          </div>
        </aside>

        {/* Main */}
        <main className={`flex-1 p-6 md:p-8 min-w-0`} style={{ backgroundColor: 'var(--color-muted)' }}>
          <div className="max-w-6xl mx-auto">
            {/* <div className={`mb-6 p-4 rounded-xl border bg-white dark:bg-[#5b3a7a]`} style={{ borderColor: 'var(--rk-border)' }}>
              <p className={`text-sm`}>
                Welcome back, <span className="font-semibold">{user.displayName?.split(' ')[0] || 'there'}</span> — continue your progress below.
              </p>
            </div> */}
            <RoutedContent
              resumeData={resumeData}
              userData={userData}
              user={user}
              db={db}
              appId={appId}
              analysisTrigger={analysisTrigger}
              coverLetterJd={coverLetterJd}
              interviewQuestionsJd={interviewQuestionsJd}
              onDataChange={handleDataChange}
              onAnalyze={initiateAnalysisFromBuilder}
              onParseComplete={handleResumeParse}
              onCareerTabChange={handleCareerTabChange}
              onGenerateCoverLetter={handleInitiateCoverLetter}
              onGenerateInterviewQuestions={handleInitiateInterviewQuestions}
              onClearCoverLetterJd={handleClearCoverLetterJd}
              onViewSavedQna={handleViewSavedQna}
              selectedHistoryItem={selectedHistoryItem}
            />
          </div>
        </main>
      </div>

      {
        isInterviewQuestionsModalOpen && (
          <InterviewQuestionsModal
            isOpen={isInterviewQuestionsModalOpen}
            onClose={() => setIsInterviewQuestionsModalOpen(false)}
            data={interviewQuestionsData}
            isLoading={isLoadingInterviewQuestions}
          />)
      }

      <PaymentSuccessModal
        isOpen={showPaymentSuccess}
        onClose={() => setShowPaymentSuccess(false)}
      />

      <ResumeUploadModal
        isOpen={isUploadModalOpen}
        onUploadComplete={() => setIsUploadModalOpen(false)}
        onParseComplete={handleResumeParse}
        onSignOut={handleSignOut}
        user={user}
      />
    </div >
  );
};

const NavItem = ({ icon, label, sublabel, active, onClick, isDarkMode = false, showText = true }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors border min-w-0 ${active
      ? 'bg-[#FFD600] text-[#0E0E0E] border-[#FFD600] shadow'
      : 'text-white hover:bg-white/10 hover:text-white border-transparent'
      }`}
  >
    <span className="flex-shrink-0">{icon}</span>
    <div className={`${showText ? 'flex-1' : 'hidden md:block md:flex-1 md:opacity-100'} min-w-0 ${showText ? '' : 'md:hidden'}`}>
      <div className="truncate">{label}</div>
      {sublabel && (
        <div className={`text-xs truncate ${active ? 'text-[#0E0E0E]/70' : 'text-white/80'
          }`}>
          {sublabel}
        </div>
      )}
    </div>
  </button>
);

const InterviewQuestionsModal = ({ isOpen, onClose, data, isLoading }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-[#3C0F6C]/70 flex items-center justify-center p-4 z-50">
      <div className="bg-[#fafafa] border border-[#d5e7f3] rounded-2xl shadow-2xl max-w-2xl w-full flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-[#d5e7f3] flex-shrink-0 flex  items-center">
          <h3 className="text-xl font-bold text-[#3C0F6C]">AI-Generated Interview Prep</h3>
          <button onClick={onClose} className="p-1 rounded-full text-[#3C0F6C] hover:bg-[#d5e7f3]">
            <XCircle size={20} />
          </button>
        </div>
        <div className="p-6 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <svg className="animate-spin h-8 w-8 text-[#3C0F6C]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="ml-4 text-[#3C0F6C]">Generating questions and answers...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {(data || []).map((item, index) => (
                <div key={index} className="rounded-xl border border-[#d5e7f3] p-4 bg-[#d5e7f3]/30">
                  <h4 className="font-bold text-[#3C0F6C] mb-2">Question {index + 1}:</h4>
                  <p className="mb-2 text-[#3C0F6C]">{item.question}</p>
                  <h5 className="font-semibold text-[#3C0F6C]/80 mb-1">Example Answer:</h5>
                  <p className="text-[#3C0F6C] whitespace-pre-wrap bg-[#fafafa] border border-[#d5e7f3] p-3 rounded-lg">{item.answer}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PaymentSuccessModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-[#3C0F6C]/70 flex items-center justify-center p-4 z-50">
      <div className="bg-[#fafafa] border border-[#d5e7f3] rounded-2xl shadow-2xl max-w-md w-full">
        <div className="p-6 border-b border-[#d5e7f3] flex items-center justify-between">
          <h3 className="text-xl font-bold text-[#3C0F6C]">Payment Successful</h3>
          <button onClick={onClose} className="p-1 rounded-full text-[#3C0F6C] hover:bg-[#d5e7f3]">
            <XCircle size={20} />
          </button>
        </div>
        <div className="p-6 text-[#3C0F6C]">
          <p className="mb-2">Thank you! Your payment was successful.</p>
          <p>Your account has been upgraded to <span className="font-semibold">Premium</span>.</p>
        </div>
        <div className="p-6 pt-0">
          <button
            onClick={onClose}
            className="w-full bg-[#3C0F6C] text-[#fafafa] font-semibold py-2.5 px-4 rounded-xl hover:bg-[#3C0F6C]/90"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <Routes>
        {/* Marketing/landing routes wrapped with global Layout */}
        <Route path="/" element={<Layout><Desktop20 /></Layout>} />
        {/* <Route path="/features" element={<Layout><Desktop21 /></Layout>} /> */}
        {/* <Route path="/ai-tools" element={<Layout><Desktop24 /></Layout>} /> */}
        <Route path="/resume-builder" element={<Layout><Desktop4 /></Layout>} />
        <Route path="/interview-prep" element={<Layout><Desktop22 /></Layout>} />
        <Route path="/interview-simulation" element={<Layout><Desktop29 /></Layout>} />
        <Route path="/cover-letter-generator" element={<Layout><Desktop23 /></Layout>} />
        <Route path="/job-matching" element={<Layout><Desktop25 /></Layout>} />
        <Route path="/resume-scoring" element={<Layout><Desktop26 /></Layout>} />
        <Route path="/signuppricing" element={<Layout><SignupPricing /></Layout>} />
        <Route path="/blogs" element={<Layout><Blogs /></Layout>} />
        <Route path="/blogs/:slug" element={<Layout><BlogDetail /></Layout>} />
        <Route path="/contactus" element={<Layout><Desktop30 /></Layout>} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/auth" element={<Auth />} />


        {/* Main App Route (product experience) with nested routes */}
        <Route path="/dashboard/*" element={<AppContent />} />
        {/* Redirect old catch-all to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Router>
  );
};

export default App;
